'use client'

import { useEffect, useRef, useState } from 'react'
import { hostedPreviewUrl } from '@/lib/widget/embed'

// Real live widget preview. The Idukki renderer is GUID-driven and has a
// standalone hosted preview page, so we show an actual rendered widget in
// a sandboxed iframe rather than a static mock. While it loads, a
// skeleton holds the frame so there is no layout shift; the skeleton's
// shimmer is dropped under prefers-reduced-motion.
//
// Pass a `guid` (and optional `bguid`) of a real collection. For the
// builder's pre-provision state, pass a curated demo collection guid so
// visitors see a live widget styled close to their choices.

export function LiveWidgetPreview({
  guid,
  bguid,
  test,
  title = 'Live widget preview',
  className,
  ratio = 'aspect-[4/3]',
}: {
  guid: string
  bguid?: string
  test?: boolean
  title?: string
  className?: string
  ratio?: string
}) {
  const [loaded, setLoaded] = useState(false)
  const frameRef = useRef<HTMLIFrameElement>(null)

  // Reset the loaded state whenever the previewed collection changes.
  useEffect(() => {
    setLoaded(false)
  }, [guid, bguid, test])

  const src = hostedPreviewUrl(guid, { bguid, test })

  return (
    <div className={`relative overflow-hidden rounded-2xl border bg-card ${ratio} ${className ?? ''}`}>
      {!loaded && (
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-secondary/60 via-card to-secondary/40 motion-safe:animate-pulse"
        />
      )}
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className="h-full w-full"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    </div>
  )
}
